import {
  computed,
  defineComponent,
  unref,
  type FunctionalComponent,
} from "vue";
import type { IDataTableBannerProps } from "./types";
import { Header } from "components/header";
import { Grid } from "components/grid";
import BasicInput from "components/form/basic-input";
import SearchIcon from "assets/img/svg/icons/search.svg?url";
import XIcon from "assets/img/svg/icons/x.svg?url";
import Icon from "components/icon";
import { twColor } from "@/utils/style";
import { tv } from "tailwind-variants";
import { uref } from "@/utils/reactive/uref";
import { HEAD_ROW_HEIGHT_PX } from "../../constants";
import { useResponsive } from "~/composables/useResponsive";

const clazz = tv({
  slots: {
    base: "bg-white p-4 space-y-4",
  },
  variants: {
    color: {
      gray: "bg-gray-50",
    },
  },
});

export const DataBanner = defineComponent<IDataTableBannerProps>(
  (props) => {
    const isLoading = computed(() => !!unref(props.isLoading));
    const responsive = useResponsive();
    const propHeader = computed(() => unref(props.banner.header));
    const fullWidthActions = computed(
      () => !!unref(props.banner.fullWidthActions),
    );
    const BodyContent = computed(() => {
      const content = unref(props.banner.body);
      if (typeof content === "function") return <content />;
      return content;
    });
    const bottomRows = computed(() => {
      const hrows = props.table.props.headRows
        ? uref(props.table.props.headRows)
        : [];
      return (
        <div>
          {hrows.map((row) => {
            const InnerContent: FunctionalComponent<any> = row.body
              ? row.body
              : () => <div />;
            return (
              <div
                class="px-4 py-2 border-gray-300 text-gray-500 text-xs font-semibold uppercase border-t bg-gray-50 flex items-center"
                style={{
                  height: HEAD_ROW_HEIGHT_PX + "px",
                }}
              >
                <InnerContent />
              </div>
            );
          })}
        </div>
      );
    });
    const actions = computed(() => props.banner.actions || []);
    const actionsLayout = computed(() =>
      actions.value.length <= 0 ||
      (responsive.isSmall.value && actions.value.length <= 1)
        ? ""
        : `repeat(${actions.value.length}, max-content)`,
    );
    const shouldInlineHeaderActions = computed(
      () =>
        responsive.isPhone.value &&
        !!propHeader.value &&
        actions.value.length > 0 &&
        !fullWidthActions.value,
    );
    const layoutStyles = computed(() => {
      if (responsive.isPhone.value) {
        return {
          display: "grid",
          gap: "1.25rem",
        };
      }
      return {
        display: "grid",
        ...(actions.value.length > 0
          ? {
              gridTemplateColumns: "1fr 1fr",
            }
          : {}),
      };
    });
    const classes = clazz({});

    return () => {
      const renderActions = (inline = false) => {
        if (actions.value.length <= 0) return null;

        return (
          <div
            class={inline ? undefined : "w-full"}
            style={{
              ...(inline
                ? {
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "1rem",
                    alignItems: "center",
                    marginLeft: "auto",
                  }
                : {
                    display: "grid",
                    gridTemplateColumns: actionsLayout.value,
                    gap: "1rem",
                    ...(responsive.isPhone.value
                      ? {}
                      : {
                          direction: "rtl",
                        }),
                  }),
            }}
          >
            {actions.value.map((action, i) => {
              return (
                <div
                  style={{
                    direction: "ltr",
                    ...(responsive.isSmall.value
                      ? {
                          width: fullWidthActions.value
                            ? "100%"
                            : "fit-content",
                        }
                      : {}),
                  }}
                  key={`action-${i}`}
                >
                  <action.body />
                </div>
              );
            })}
          </div>
        );
      };

      return (
        <div>
          <div
            class={classes.base({ color: props.variant as any })}
            style={[props.banner.style || {}]}
          >
            <div
              class={[
                "w-full",
                ...(props.table.options.search || props.banner.body
                  ? ["pb-4 border-b border-gray-200"]
                  : []),
              ]}
            >
              <div
                class="w-full"
                style={layoutStyles.value}
              >
                {shouldInlineHeaderActions.value ? (
                  <>
                    <div class="flex flex-wrap items-start gap-5 w-full">
                      <div class="flex-1 min-w-0">
                        <Header
                          title={propHeader.value?.title}
                          subtitle={propHeader.value?.subtitle}
                          image={propHeader.value?.image}
                          size={propHeader.value?.size || "lg"}
                        />
                      </div>
                      {renderActions(true)}
                    </div>
                    {props.banner.image && <props.banner.image />}
                  </>
                ) : (
                  <>
                    {propHeader.value && (
                      <Header
                        title={propHeader.value.title}
                        subtitle={propHeader.value.subtitle}
                        image={propHeader.value.image}
                        size={propHeader.value.size || "lg"}
                      />
                    )}
                    {props.banner.image && <props.banner.image />}
                    {renderActions()}
                  </>
                )}
              </div>
            </div>
            {props.table.options.search?.enabled && !isLoading.value ? (
              <Grid
                class="w-full text-gray-500"
                noDefaultLayout
                direction="ltr"
                alignItems="center"
                gap="0.75rem"
                columns={responsive.isSmall.value ? "100%" : `max-content${props.table.options.search?.suffix ? " max-content" : ""}`}
              >
                <div
                  style={{
                    position: "relative",
                    width: responsive.isSmall.value ? "100%" : "265px",
                  }}
                >
                  <BasicInput
                    disabled={isLoading.value}
                    iconLeft={{
                      src: SearchIcon,
                      size: "1.25rem",
                      stroke: twColor("gray-400"),
                    }}
                    onValueChange={(val) => {
                      props.table.search.setQueryString(val);
                      props.table.state.setQueryString(val || "");
                    }}
                    placeholder={
                      props.table.options.search.placeholder || "Search..."
                    }
                    type="text"
                    value={props.table.search.queryString.value || ""}
                  />
                  {props.table.search.queryString.value ? (
                    <button
                      onClick={() => {
                        props.table.search.setQueryString(undefined);
                        props.table.state.setQueryString("");
                      }}
                      style={{
                        position: "absolute",
                        right: "0.5rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        background: "none",
                        border: "none",
                        padding: "2px",
                      }}
                    >
                      <Icon
                        src={XIcon}
                        size="0.875rem"
                        stroke={twColor("gray-400")}
                      />
                    </button>
                  ) : null}
                </div>
                {props.table.options.search?.suffix?.()}
              </Grid>
            ) : null}
            {BodyContent.value}
          </div>
          {props.table.props.headRows ? bottomRows.value : null}
        </div>
      );
    };
  },
  { props: ["banner", "table", "variant", "isLoading"] },
);
